import { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import classNames from 'classnames';

import Button from '../../Button';
import { TestMetadata } from '../../types';
import { useResponsiveNavBar } from '../NavBarContext';
import NavBarItem from '../NavBarItem';
import ControlsMenu from './ControlsMenu';

import './Controls.scss';

export interface ControlsProps {
  /** Classname for Controls section */
  className?: string;
  /** TestMetadata for container and expand button */
  testMetadata?: {
    container?: TestMetadata;
    expandButton?: TestMetadata;
  };
  /** Title to display on hamburger menu header */
  controlsMenuTitle?: string;
  /** Environment name to display on hamburger menu header */
  controlsMenuEnvironment?: string;
  /** Renders a custom controls menu body when hamburger action is clicked. */
  renderControlsMenuBody?: (closeAction: () => void) => ReactNode;
  /** Renders children and also give them access to close action (useful to close the controls menu when a link is clicked */
  renderControlsChildren?: (closeAction: () => void) => ReactNode;
  /** controlsMenuTitle prop becomes actionable through this callback */
  controlsMenuBackAction?: () => void;
  /** controlsMenu close callback */
  onCloseControlsMenu?: () => void;
}

const Controls: FC<ControlsProps> = ({
  className,
  testMetadata,
  children,
  controlsMenuTitle,
  controlsMenuEnvironment,
  renderControlsMenuBody,
  renderControlsChildren,
  controlsMenuBackAction,
  onCloseControlsMenu,
}) => {
  const { getBreakpointClassName, useMobileDrawer } = useResponsiveNavBar();
  const [controlsMenuOpen, setControlsMenuOpen] = useState<boolean>(false);

  const hamburgerClick = useCallback(() => {
    setControlsMenuOpen(!controlsMenuOpen);
  }, [controlsMenuOpen]);

  const controlsClassNames = useMemo(
    () =>
      classNames(
        getBreakpointClassName('sb-controls'),
        {
          [getBreakpointClassName('sb-controls__collapsed')]:
            !controlsMenuOpen && !useMobileDrawer,
          [getBreakpointClassName('sb-controls__expanded')]:
            controlsMenuOpen && !useMobileDrawer,
          'sb-controls__open': controlsMenuOpen,
        },
        className,
      ),
    [className, controlsMenuOpen, getBreakpointClassName, useMobileDrawer],
  );

  const closeControlsMenu = useCallback(() => {
    if (!controlsMenuOpen) {
      return;
    }

    setControlsMenuOpen(false);

    if (onCloseControlsMenu) {
      onCloseControlsMenu();
    }
  }, [controlsMenuOpen, onCloseControlsMenu]);

  // We provide closeControlsMenu function to the children so that they can close the menu when a link is clicked.
  // For backwards compatibility, we also render the children if renderControlsChildren is not provided.
  const controlsChildren = renderControlsChildren
    ? renderControlsChildren(closeControlsMenu)
    : children;

  return (
    <>
      <NavBarItem className={controlsClassNames} {...testMetadata?.container}>
        {controlsChildren}
      </NavBarItem>

      {controlsChildren && (
        <Button
          className={getBreakpointClassName('sb-controls__button')}
          iconType={!controlsMenuOpen ? 'fa-bars' : 'fa-times'}
          iconOnly
          labelText="Toggle Controls Section"
          styleType="link"
          sizeType="large"
          {...testMetadata?.expandButton}
          onClick={hamburgerClick}
          role="button"
        />
      )}

      {useMobileDrawer && (
        <ControlsMenu
          isVisible={controlsMenuOpen}
          onClose={closeControlsMenu}
          headerTitle={controlsMenuTitle}
          environment={controlsMenuEnvironment}
          renderMenu={renderControlsMenuBody}
          backAction={controlsMenuBackAction}
        >
          {controlsChildren}
        </ControlsMenu>
      )}
    </>
  );
};

export default Controls;
